import yasml from '@thirtytech/yasml';
import {
  searchParamsHistoryAtom,
  searchResultsAtom,
  selectedEventAtom,
  selectedMergedListingAtom,
  selectedSearchEventsAtom,
  store,
  updateEventAtom,
  updateListingsAtom,
} from '../../data/atoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CellClickedEvent,
  ColDef,
  ColumnEvent,
  SortChangedEvent,
  ColumnState,
  GridReadyEvent,
  ICellRendererParams,
  RowDataUpdatedEvent,
  RowSelectedEvent,
} from 'ag-grid-community';
import { BrokerNerdsBarkerCoreModelsInventoryEvent, getApiInventory } from '../../api';
import { DateFormats } from '../../utils/globals';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { AgGridReact } from 'ag-grid-react';
import { useDidUpdate, useLocalStorage } from '@mantine/hooks';
import { useInventoryHeader } from './Inventory.Header.hooks';
import { useRuleState } from '../../data/Rule.state';
import { TenantIdentifier } from './TenantIdentifier';
import dayjs from 'dayjs';
import { isDtiHosted } from '../../utils/whitelabel-consts';
import { TenantIdEventId } from '../../models/tenantIdListingId';
import { SelectionCell, SelectionHeader } from './Inventory.SelectionCell';

export const onColumnMoved = new Event('onColumnMoved');
export const onColumnSorted = new Event('onColumnSorted');

export function formatInventoryEventGridDate(event: Pick<BrokerNerdsBarkerCoreModelsInventoryEvent, 'localDateTime'> | null | undefined): string {
  return event?.localDateTime ? formatDate(event.localDateTime, DateFormats.GridFull) : '';
}

function InventoryEventState() {
  const gridRef = useRef<AgGridReact>(null);
  const { showSettings, showBulkOptions } = useInventoryHeader('showSettings', 'showBulkOptions');
  const { validateDirtyRuleState, triggerSaveRule } = useRuleState('validateDirtyRuleState', 'triggerSaveRule');
  const [gridReady, setGridReady] = useState(false);
  const [selectedEvents, setSelectedEvents] = useAtom(selectedSearchEventsAtom);
  const selectedEvent = useAtomValue(selectedEventAtom);
  const selectedListing = useAtomValue(selectedMergedListingAtom);
  const searchResults = useAtomValue(searchResultsAtom);
  const events = useMemo(() => searchResults?.events || [], [searchResults]);
  const updateEvent = useSetAtom(updateEventAtom);
  const updateListings = useSetAtom(updateListingsAtom);
  const showSort = showSettings;
  const defaultColumnSettingDefs = useMemo<ColDef>(
    () => ({
      width: 170,
      flex: 0,
      resizable: showSort,
      suppressHeaderMenuButton: true,
      suppressMovable: !showSort,
      sortable: showSort,
      suppressHeaderFilterButton: true,
      filter: true,
    }),
    [showSort],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      (window as any).gridRef = event;
      // Handles hot reloading
      if (event.api.getSelectedNodes().length === 0) {
        // Precompute a Set of selected eventIds so the per-node check is O(1)
        // instead of selectedEvents.find(...) per node (O(n*m)). Membership-only
        // test, so behavior is identical.
        const selectedEventIds = new Set(selectedEvents.map((x) => x.eventId));
        gridRef.current?.api.forEachNode((node) => {
          if (selectedEventIds.has(node.data.eventId)) {
            node.setSelected(true);
          }
        });
      }
      setGridReady(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Need this for show dragCell checkbox
  useDidUpdate(() => {
    gridRef.current?.api.refreshCells();
  }, [showBulkOptions]);

  const [firstRender, setFirstRender] = useState(true);

  const onRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent<BrokerNerdsBarkerCoreModelsInventoryEvent>) => {
      if (selectedListing && firstRender) {
        const node = event.api.getRowNode(TenantIdEventId.create(selectedListing.tenantId, selectedListing.eventId).toString());
        if (node && node.data) {
          event.api.deselectAll();
          node.setSelected(true);
        }
        setSelectedEvents([selectedListing.event]);
      } else if (!selectedListing && selectedEvent && firstRender) {
        const node = event.api.getRowNode(TenantIdEventId.create(selectedEvent.tenantId, selectedEvent.eventId).toString());
        if (node && node.data) {
          event.api.deselectAll();
          node.setSelected(true);
        }
        setSelectedEvents([selectedEvent]);
      } else if (selectedEvents.length === 0) {
        const firstNode = event.api.getRenderedNodes()[0];
        if (firstNode && firstNode.data) {
          firstNode.setSelected(true);
          setSelectedEvents([firstNode.data!]);
        }
      }
      if (firstRender) {
        setFirstRender(false);
      }
    },
    [firstRender, selectedEvent, selectedEvents.length, selectedListing, setSelectedEvents],
  );

  const onRowSelectionChanged = useCallback(
    (params: RowSelectedEvent<BrokerNerdsBarkerCoreModelsInventoryEvent>) => {
      const { rowIndex } = params;
      const el = document.querySelector(`[row-index="${rowIndex}"]`);
      if (params.data) {
        el?.setAttribute('aria-label', `Event ${params?.data.eventId} row`);
      }
      const selectedRows = params.api.getSelectedRows();
      setSelectedEvents(selectedRows);
    },
    [setSelectedEvents],
  );

  const [eventGridState, setEventGridState, clearEventGridState] = useLocalStorage<ColumnState[]>({
    key: 'inventory-grid-event-state',
    getInitialValueInEffect: false,
  });

  const trySaveGridState = useCallback(
    (event: ColumnEvent | SortChangedEvent) => {
      if (gridReady) {
        const state = event.api?.getColumnState()!;
        setEventGridState(state);
        if (event.type === 'columnMoved') {
          document.dispatchEvent(onColumnMoved);
        }
        if (event.type === 'sortChanged') {
          document.dispatchEvent(onColumnSorted);
        }
      }
    },
    [gridReady, setEventGridState],
  );

  const searchParams = useAtomValue(searchParamsHistoryAtom);

  const fetchSingleEventAndListings = useCallback(
    (eventId: string, tenantId: string) => {
      if (searchParams) {
        getApiInventory(
          {
            ...searchParams,
            eventIds: [eventId],
          },
          {
            headers: {
              'x-tenant-id': tenantId,
            },
          },
        ).then((result) => {
          if (result.data.events.length === 1) {
            updateEvent(result.data.events[0]);
            updateListings(result.data.listings);
          }
        });
      }
    },
    [searchParams, updateEvent, updateListings],
  );
  const onCellClickedWhenDirty = useCallback(
    async (gridEvent: CellClickedEvent<BrokerNerdsBarkerCoreModelsInventoryEvent>) => {
      // In bulk mode the dragCell checkbox drives multi-select via SelectionCell's own onClick.
      // Bail out for that column so we don't deselectAll + single-select the clicked row, which
      // would override the checkbox toggle (the cell click bubbles to AG-Grid's native listener,
      // which React's stopPropagation can't cancel). Mirrors the main Inventory grid's guard.
      if (showBulkOptions && gridEvent.column.getColId() === 'dragCell') {
        return;
      }
      if ((await validateDirtyRuleState()).resolve) {
        await triggerSaveRule();
        // closeExpandedNode(selectedListingId);
        // setSelectedListingId(gridEvent.node.data.tenantIdListingId);
        if (gridEvent.node.data) {
          setSelectedEvents([gridEvent.node.data]);
          gridEvent.api.deselectAll();
          gridEvent.node.setSelected(true);
          fetchSingleEventAndListings(gridEvent.node.data.eventId, gridEvent.node.data.tenantId);
          requestAnimationFrame(() => {
            gridRef.current?.api.ensureNodeVisible(gridEvent.node);
          });
        }
      }
    },
    [fetchSingleEventAndListings, setSelectedEvents, showBulkOptions, triggerSaveRule, validateDirtyRuleState],
  );

  type BNInventoryColumn = ColDef<BrokerNerdsBarkerCoreModelsInventoryEvent> & {
    exclude?: boolean;
    allowSorting?: boolean;
    sort?: 'asc' | 'desc' | null;
    sortIndex?: number | null;
  };
  const defaultColumnDefs: BNInventoryColumn[] = useMemo(
    () =>
      [
        {
          colId: 'dragCell',
          exclude: true,
          allowSorting: false,
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          // `hide` is set reactively in the columnDefs memo below. This memo has [] deps, so
          // reading showBulkOptions here would freeze it at its initial value (false) and the
          // selection column would never appear when bulk mode is toggled on.
          valueGetter: () => null,
          cellRenderer: SelectionCell,
          headerComponent: SelectionHeader,
        },
        {
          headerName: 'Company',
          field: 'tenantId',
          hide: true,
          width: 140,
          cellStyle: { display: 'flex' },
          cellRenderer: (params: ICellRendererParams<BNInventoryColumn>) => <TenantIdentifier tenantId={params.value} />,
        },
        { headerName: 'Name', field: 'name', flex: 1, minWidth: 260, sort: undefined },
        {
          headerName: 'Date',
          field: 'localDateTime',
          sortIndex: 0,
          sort: 'asc',
          valueGetter: (params) => (params.data?.utcDateTime ?? params.data?.localDateTime)?.toString(), // This is to give a constant value to prevent cell flashing / rerendering
          valueFormatter: (params) => formatInventoryEventGridDate(params.data),
          comparator: (a, b) => dayjs(a).unix() - dayjs(b).unix(),
          cellStyle: { fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
        },
        {
          headerName: 'Day',
          colId: 'day',
          valueGetter: (params) => (params.data?.localDateTime ? dayjs(params.data.localDateTime).format('ddd') : ''),
          width: 70,
          hide: true,
        },
        { headerName: 'Venue', field: 'venue.name', minWidth: 200 },
        { headerName: 'Performer', field: 'performer.name', minWidth: 200, hide: true },
        {
          headerName: 'Tickets Sold',
          field: 'numTicketsSold',
          hide: true,
          width: 100,
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: 'Cost',
          minWidth: 110,
          width: 110,
          field: 'openCost',
          valueFormatter: (params) => (params.data?.openCost ? formatCurrency(params.data.openCost) : ''),
          cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
        },
        {
          headerName: 'Listings',
          field: 'openListings',
          width: 100,
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: 'Tickets',
          field: 'openTickets',
          width: 100,
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: 'Last Sold',
          field: 'lastSoldAt',
          width: 100,
          valueGetter: (params) =>
            params.data?.lastSoldAt
              ? dayjs().startOf('day').toDate().getTime() === dayjs(params.data.lastSoldAt).startOf('day').toDate().getTime()
                ? formatDate(params.data.lastSoldAt, DateFormats.GridTimeOnly)
                : formatDate(params.data.lastSoldAt, DateFormats.GridShort)
              : '',
          cellStyle: { fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
        },
        {
          headerName: 'Contingent',
          field: 'vendorProperties.isContingent',
          width: 110,
          hide: true,
        },
        {
          headerName: 'Unbroadcasted',
          field: 'isBroadCasting',
          valueGetter: (params) => {
            if (!params?.data?.eventId) return 0;
            const unbroadcastedEventListings = store
              .get(searchResultsAtom)
              ?.listings.filter((listing) => listing.eventId === params?.data?.eventId && listing.tenantId === params?.data?.tenantId && !listing.isBroadcasting);
            return unbroadcastedEventListings?.reduce((sum, listing) => sum + listing.quantityRemaining, 0);
          },
          headerTooltip: 'Tickets Not Broadcast',
          width: 135,
          hide: true,
          cellStyle: { textAlign: 'right' },
        },
        // { headerName: 'Links', field: 'links' },
        // { headerName: 'Monitors', field: 'monitors' },
        // { headerName: 'Number of Comments', field: 'numComments' },
        // { headerName: 'Status ID', field: 'statusId' },
        // { headerName: 'Tags', field: 'tags' },
        // { headerName: 'Tenant ID', field: 'tenantId' },
        // { headerName: 'Viewed At', field: 'viewedAt' },
        // { headerName: 'Viewed By', field: 'viewedBy' },
        // { headerName: 'Weather Forecast', field: 'weatherForecast' },
      ] as BNInventoryColumn[],
    [],
  );
  const columnDefs: ColDef<BrokerNerdsBarkerCoreModelsInventoryEvent>[] = useMemo(() => {
    // Hoist the dragCell filter out of the per-column map (it was rebuilding the
    // ['dragCell'] array and re-filtering eventGridState for every column).
    const savedStateColumns = eventGridState?.filter((x) => x.colId !== 'dragCell');
    // Precompute colId -> index over the full eventGridState so the sort
    // comparator is an O(1) Map lookup instead of findIndex per comparison.
    // Mirrors findIndex exactly: first match wins, and a colId that is not
    // present resolves to -1 (matching findIndex's -1, not 0) before `?? 0`.
    const colIdIndex = new Map<string | undefined, number>();
    eventGridState?.forEach((x, i) => {
      if (!colIdIndex.has(x.colId)) colIdIndex.set(x.colId, i);
    });
    const indexOfColId = (colId: string | undefined) => (eventGridState === undefined ? undefined : colIdIndex.has(colId) ? colIdIndex.get(colId)! : -1);
    return defaultColumnDefs
      .filter((col) => !(!isDtiHosted && col.headerName === 'Contingent'))
      .map((col) => {
        // Clears properties before giving it to AG-Grid to avoid warnings.
        const { allowSorting: _allowSorting, exclude: _exclude, ..._col } = col;
        const savedState = { ...savedStateColumns?.find((y) => y.colId === col.colId || y.colId === col.field) };
        const merged = { ..._col, ...savedState } as ColDef<BrokerNerdsBarkerCoreModelsInventoryEvent>;
        // The dragCell holds the selection checkbox and only appears in bulk mode. Computed here
        // (where showBulkOptions is a memo dep) so toggling bulk mode shows/hides the column —
        // this restores the pre-v35 behavior that set _col.hide/checkboxSelection in this map.
        if (merged.colId === 'dragCell') {
          merged.hide = !showBulkOptions;
        }
        return merged;
      })
      .sort((a, b) => {
        const aIndex = indexOfColId(a.colId);
        const bIndex = indexOfColId(b.colId);
        return (aIndex ?? 0) - (bIndex ?? 0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulkOptions, showSettings]);
  return {
    gridRef,
    onGridReady,
    gridReady,
    trySaveGridState,
    events,
    defaultColumnSettingDefs,
    defaultColumnDefs,
    columnDefs,
    onRowSelectionChanged,
    onRowDataUpdated,
    eventGridState,
    clearEventGridState,
    onCellClickedWhenDirty,
  };
}

export const { Provider: InventoryEventStateProvider, useSelector: useInventoryEvents } = yasml(InventoryEventState);
